import type { at } from '../generated/Beans';
import { create } from 'zustand';
import { PortletStoreApi } from '../App';
import { getWebsocketInterface } from '../communication/websocket';
import { CallStateApi } from './call.store';
import { getCallStrategy } from '../call/callstrategy';
import { produce } from 'immer';
import { intl, NotificationType, notify } from 'ehs-react';
import {
  getBrowserLanguage,
  getParticipantNameList
} from 'virtual-visit-common';
import { ViewStateStoreApi } from './viewState.store';
import { showErrorWithReloadButton } from '../utils/ComponentUtils';
import { ChatStoreApi } from './chat.store';
import {
  grantBioAnalysisPermission,
  inviteGuestToCall,
  recreateGuestLink
} from '../communication/ajax';
import type { icModel } from 'generated-ts-common';
import { AzureCallStrategy } from '../call/azurecallstrategy';
import { getCorrectNameForChatAuthor } from '../utils/utils';
import { showBioAnalysisConfirmationDialog } from '../components/VideoConf/VideoConference';
type SocketDataBean = at.sense.portlets.saf.virtualvisitpatient.model.SocketDataBean;
type RegistrationFormFields = at.sense.portlets.saf.virtualvisitpatient.beans.config.RegistrationFormFields;
type Institution = at.sense.portlets.saf.virtualvisitpatient.model.Institution;
type WebSocketOperation = at.sense.portlets.saf.virtualvisitpatient.model.WebSocketOperation;
type ParticipantBean = icModel.saf.infrastructure.model.virtualvisit.ParticipantBean;
type GuestBean = at.sense.saf.infrastructure.portalutil.virtualvisit.guest.GuestBean;
type GuestData = at.sense.portlets.saf.virtualvisitpatient.model.GuestData;
type AzureUserBean = icModel.saf.infrastructure.model.virtualvisit.AzureUserBean;

export type WaitingRoomTab = 'WAITING_ROOM' | 'CHAT';

export interface WaitingRoomStore {
  visitInfo?: SocketDataBean;
  sessionInitalized: boolean;
  loading: boolean;
  initWebsocket: () => Promise<void>;
  cleanupWebsocket: () => Promise<void>;
  registerNewVisit: () => Promise<void>;
  registerGuest: () => Promise<void>;
  rejoinSession: () => Promise<void>;
  rejoinGuestSession: () => Promise<void>;
  initSession: (
    mandatoryFieldForRegistration: RegistrationFormFields
  ) => Promise<void>;
  leaveWaitingList: () => Promise<void>;
  inviteGuests: () => Promise<void>;
  sendVisitStatus: () => Promise<void>;
  waitingRoomTab: WaitingRoomTab;
  participants: ParticipantBean[];
  currentGuestParticipant?: ParticipantBean;
  recreateGuestLink: (visitId: string, guestToken: string) => Promise<void>;
  grantBioAnalysisPermission: () => Promise<void>;
  guestBeans: GuestBean[];
  inviteLinkDrawerOpen: boolean;
  toggleInviteLinkDrawerOpen: () => void;
}

function setSessionData(
  queueId: string,
  enteredIdentificationNumber?: string,
  enteredFamilyName?: string,
  selectedInstitution?: Institution,
  visitId?: string,
  guestData?: GuestData
): void {
  sessionStorage.setItem('queueId', queueId);
  if (enteredIdentificationNumber) {
    sessionStorage.setItem(
      'enteredIdentificationNumber',
      enteredIdentificationNumber
    );
  }
  if (enteredFamilyName) {
    sessionStorage.setItem('enteredFamilyName', enteredFamilyName);
  }
  sessionStorage.setItem(
    'selectedInstitution',
    JSON.stringify(selectedInstitution)
  );

  if (visitId) {
    sessionStorage.setItem('visitId', visitId);
  }
  if (guestData?.urlToken) {
    sessionStorage.setItem('urlToken', guestData.urlToken);
  }
  if (guestData?.rejoinCode) {
    sessionStorage.setItem('rejoinCode', guestData.rejoinCode);
  }
}

function updateParticipants(participants: ParticipantBean[]) {
  const vsd = ViewStateStoreApi.getState().viewStateData;
  const token = vsd?.guestData?.urlToken;
  let curPart = undefined;
  if (token) {
    curPart = participants.find(p => p.urlToken === token);
  }
  WaitingRoomStoreApi.setState({
    participants: participants,
    currentGuestParticipant: curPart
  });

  updateParticipantNameList(participants);
}

function updateParticipantNameList(participants: ParticipantBean[]) {
  const partNames = getParticipantNameList(
    participants,
    getCorrectNameForChatAuthor(
      ViewStateStoreApi.getState().viewStateData?.patientData
    ),
    WaitingRoomStoreApi.getState().visitInfo?.visit?.user?.fullname ??
      intl('virtualvisitoverviewportlet.chat.tab.physician')
  );
  console.log('setParticipantNameList', partNames);
  if (getCallStrategy() instanceof AzureCallStrategy) {
    (getCallStrategy() as AzureCallStrategy).setParticipantNameList(partNames);
  }
}

function setPatientActiveStatus(
  participants: ParticipantBean[]
): ParticipantBean[] {
  participants
    .filter(p => p.type === 'PATIENT')
    .forEach(p => (p.active = true));
  return participants;
}
function updateCallStrategyConfig(
  roomId: string | undefined,
  azureUser: AzureUserBean | undefined
): void {
  if (roomId && azureUser) {
    CallStateApi.setState({
      callStrategyConfig: {
        roomId: roomId,
        userIdentifier: azureUser?.userId,
        token: azureUser?.token
      }
    });
  }
}

let pauseCallPromise = Promise.resolve();

export const [
  useWaitingRoomStore,
  WaitingRoomStoreApi
] = create<WaitingRoomStore>((set, get) => ({
  visitInfo: undefined,
  loading: false,
  sessionInitalized: false,
  waitingRoomTab: 'WAITING_ROOM',
  participants: [],
  guestBeans: [],
  inviteLinkDrawerOpen: false,
  toggleInviteLinkDrawerOpen: () => {
    set(state => (state.inviteLinkDrawerOpen = !state.inviteLinkDrawerOpen));
  },
  initWebsocket: async () => /* NOSONAR - function is as easy as it can get */ {
    console.log('init websocket');
    const websocketUrl = (PortletStoreApi.getState() as any).websocketUrl;
    getWebsocketInterface().messageListeners.push(socketData => {
      switch (socketData.callbackStatus) {
        case 'LEAVE_ROOM':
          console.log(['@@@callBack_LEAVE_ROOM:', socketData]);
          sessionStorage.clear();
          location.reload();
          break;
        case 'START_CALL': {
          console.log(['@@@callBack_START_CALL', socketData]);
          const doStartCall = async () => {
            await pauseCallPromise; // wait for pause call to finish
            CallStateApi.setState({
              callStrategyConfig: getCallStrategy().getCallStrategyConfig(
                socketData
              )
            });
            //only forward to call if patient or approved guest
            if (
              !get().currentGuestParticipant ||
              get().currentGuestParticipant?.status === 'VALID_APPROVED'
            ) {
              ViewStateStoreApi.getState().nextState();
            }
          };
          doStartCall();
          break;
        }
        case 'END_CALL':
          console.log(['@@@callBack_END_CALL', socketData]);
          getCallStrategy().setPreventPhysicianLeftNotification(true);
          getCallStrategy().stopCall();
          ViewStateStoreApi.setState(
            produce(state => {
              if (state.viewStateData) {
                state.viewStateData.physicianEndedCall = true;
              }
            })
          );
          ViewStateStoreApi.getState().nextState();
          sessionStorage.clear();
          break;
        case 'PAUSE_CALL':
        case 'PAUSE_CALL_BY_DISCONNECTION': {
          console.log(['@@@callBack_PAUSE_CALL', socketData]);
          const doPause = async () => {
            try {
              if (
                ViewStateStoreApi.getState().viewStateData?.state !==
                'WAITING_ROOM'
              ) {
                await ViewStateStoreApi.getState().previousState();
                getCallStrategy().setPreventPhysicianLeftNotification(true);
                await getCallStrategy().stopCall();
              }
            } catch (e) {
              console.log('error during pause call', e);
            }
          };
          pauseCallPromise = doPause();

          if (socketData.callbackStatus === 'PAUSE_CALL_BY_DISCONNECTION') {
            notify({
              message: intl(
                'virtualvisitpatientportlet.view.call.physicianleft'
              ),
              onClose: () => console.log('physician left closed'),
              title: intl('common.note'),
              type: NotificationType.ERROR
            });
          }
          break;
        }
        case 'UPDATE_QUEUE':
          console.log(['@@@callBack_UPDATE_QUEUE', socketData]);
          set(
            produce(state => {
              state.visitInfo.queueInfoBean = socketData.queueInfoBean;
            })
          );
          break;
        case 'IN_QUEUE':
          console.log(['@@@callBack_IN_QUEUE', socketData]);
          ViewStateStoreApi.setState(
            produce(state => {
              if (state.viewStateData) {
                state.viewStateData.queueId = socketData.queueInfoBean.queueId;
                if (state.viewStateData.guestData) {
                  state.viewStateData.guestData.rejoinCode =
                    socketData.guestData?.rejoinCode;
                }
                state.viewStateData.visitId = socketData.visit.visitId;
              }
            })
          );
          set(
            produce(state => {
              state.visitInfo = socketData;
            })
          );

          updateParticipants(socketData.visit.participants);
          updateCallStrategyConfig(
            socketData.visit?.azureRoom?.roomId,
            get().currentGuestParticipant?.azureUser
          );

          //session storage
          setSessionData(
            socketData.queueInfoBean.queueId,
            ViewStateStoreApi.getState().viewStateData?.patientData
              .patientIdentificator,
            ViewStateStoreApi.getState().viewStateData?.patientData.familyName,
            ViewStateStoreApi.getState().viewStateData?.selectedInstitution,
            ViewStateStoreApi.getState().viewStateData?.visitId,
            ViewStateStoreApi.getState().viewStateData?.guestData
          );

          set(state => (state.sessionInitalized = true));
          set(state => (state.loading = false));

          set(state => (state.loading = false));

          break;
        case 'VISIT_REGISTRATION_ERROR':
          console.log(['@@@callBack_VISIT_REGISTRATION_ERROR', socketData]);
          set(state => (state.loading = false));
          showErrorWithReloadButton(
            intl('virtualvisitpatientportlet.register.visit.error.message') +
              ' ' +
              intl('virtualvisitpatientportlet.waiting.room.please.retry'),
            intl('virtualvisitpatientportlet.register.visit.error.title')
          );
          break;
        case 'SET_BACK_IN_QUEUE': {
          console.log(['@@@callBack_SET_BACK_IN_QUEUE', socketData]);

          const patientData = ViewStateStoreApi.getState().viewStateData
            ?.patientData;
          console.log('patientData', patientData, socketData.patientData);

          ViewStateStoreApi.setState(
            produce(state => {
              state.viewStateData.patientData = socketData.patientData;
              state.viewStateData.visitId = socketData.visit.visitId;
            })
          );

          if (socketData.operation === 'REJOIN_SESSION') {
            const part = setPatientActiveStatus(socketData.visit.participants);
            WaitingRoomStoreApi.setState(
              produce(state => {
                state.participants = part;
              })
            );
          }
          updateCallStrategyConfig(
            socketData.visit?.azureRoom?.roomId,
            get().currentGuestParticipant?.azureUser
          );

          console.log(
            'patientData after',
            ViewStateStoreApi.getState().viewStateData?.patientData
          );
          console.log(
            'participant data after',
            WaitingRoomStoreApi.getState().participants
          );

          //session storage
          setSessionData(
            socketData.queueInfoBean.queueId,
            ViewStateStoreApi.getState().viewStateData?.enteredRegistrationData
              .identificationNumber,
            ViewStateStoreApi.getState().viewStateData?.enteredRegistrationData
              .familyName,
            ViewStateStoreApi.getState().viewStateData?.selectedInstitution,
            ViewStateStoreApi.getState().viewStateData?.visitId,
            ViewStateStoreApi.getState().viewStateData?.guestData
          );

          WaitingRoomStoreApi.setState(
            produce(state => {
              state.visitInfo = socketData;
              state.sessionInitalized = true;
              state.loading = false;
            })
          );

          ChatStoreApi.getState().setChatMessages(
            socketData.chatMessages || []
          );

          updateParticipants(socketData.visit.participants);

          console.log(
            'check if azureSocketDataBeanConfig is set',
            socketData.azureSocketDataBeanConfig
          );
          if (socketData.azureSocketDataBeanConfig) {
            console.log('resume call!!!!');
            CallStateApi.setState({
              callStrategyConfig: socketData.azureSocketDataBeanConfig
            });
            ViewStateStoreApi.getState().nextState();
          }
          break;
        }
        case 'SET_BACK_IN_QUEUE_ERROR':
          console.log(['@@@callBack_SET_BACK_IN_QUEUE_ERROR', socketData]);
          set(state => (state.loading = false));
          showErrorWithReloadButton(
            intl('virtualvisitpatientportlet.rejoin.session.error.message') +
              ' ' +
              intl('virtualvisitpatientportlet.waiting.room.please.retry'),
            intl('virtualvisitpatientportlet.waiting.room.rejoin.error')
          );
          sessionStorage.clear();
          break;
        case 'RATE_LIMITING_ERROR':
          console.log(['@@@callBack_RATE_LIMITING_ERROR', socketData]);
          set(state => (state.loading = false));
          showErrorWithReloadButton(
            intl(
              'virtualvisitpatientportlet.rejoin.session.error.message.rateLimiter'
            )
          );
          sessionStorage.clear();
          break;
        case 'PATIENT_IDENTIFIED':
          console.log(['@@@callBack_PATIENT_IDENTIFIED', socketData]);
          set(
            produce(state => {
              state.visitInfo.patientData.givenName =
                socketData.patientData.givenName;
              state.visitInfo.patientData.familyName =
                socketData.patientData.familyName;
            })
          );
          ViewStateStoreApi.setState(
            produce(state => {
              state.viewStateData.patientData.givenName =
                socketData.patientData.givenName;
              state.viewStateData.patientData.familyName =
                socketData.patientData.familyName;
            })
          );
          updateParticipantNameList(get().participants || []);
          break;
        case 'FINISH_CALL_FOR_PATIENT':
          ViewStateStoreApi.getState().nextState();
          break;
        case 'UPDATED_VISIT':
          console.log('@@@callBack_UPDATED_VISIT', socketData);
          set(
            produce(state => {
              if (state.visitInfo) {
                state.visitInfo.visit = socketData.visit;
              }
            })
          );
          console.log('visit info after', get().visitInfo);
          updateParticipants(socketData.visit.participants);
          break;
        case 'REQUEST_BIO_ANALYSIS_PERMISSION':
          showBioAnalysisConfirmationDialog();
          break;
        default:
          console.log(['@@@callBack_UNKNOWN', socketData]);
          break;
      }
    });

    const onError = () => {
      if (
        ViewStateStoreApi.getState().viewStateData?.state !== 'FINAL_SCREEN'
      ) {
        showErrorWithReloadButton(
          intl(
            'virtualvisitpatientportlet.websocket.error.message.notregistered.content'
          ),
          intl('virtualvisitpatientportlet.waiting.room.connection.error')
        );
      }
    };
    const onReconnected = () => {
      console.log('rejoin session after reconnect');
      get().rejoinSession();
    };
    await getWebsocketInterface().connect(websocketUrl, onError, onReconnected);
  },
  cleanupWebsocket: async (): Promise<void> => {
    await getWebsocketInterface().disconnect();
  },
  registerNewVisit: async (): Promise<void> => {
    console.log('register new visit');

    const vsd = ViewStateStoreApi.getState().viewStateData;
    const appointmentId = vsd?.appointmentId;
    const institutionId = vsd?.selectedInstitution.id;
    const data = vsd?.patientData;

    const socketData = {
      operation: 'NEW_VISIT' as WebSocketOperation,
      appointmentId: appointmentId,
      institutionId: institutionId,
      patientData: data,
      browserLanguage: getBrowserLanguage()
    };
    await getWebsocketInterface().send(socketData);
  },
  registerGuest: async (): Promise<void> => {
    console.log('register guest');
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const appointmentId = vsd?.appointmentId;
    const institutionId = vsd?.selectedInstitution?.id;
    const data = vsd?.patientData;
    const guestData = vsd?.guestData;
    const visitId = vsd?.visitId;

    const socketData = {
      operation: 'REGISTER_GUEST' as WebSocketOperation,
      appointmentId: appointmentId,
      institutionId: institutionId,
      patientData: data,
      guestData: guestData,
      visitId: visitId,
      browserLanguage: getBrowserLanguage()
    };
    await getWebsocketInterface().send(socketData);
  },
  rejoinSession: async (): Promise<void> => {
    console.log('rejoin session');
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const data = vsd?.patientData;

    if (vsd && vsd.state !== 'FINAL_SCREEN') {
      const socketData = {
        operation: 'REJOIN_SESSION' as WebSocketOperation,
        queueId: vsd.queueId,
        enteredRegistrationData: {
          identificationNumber:
            vsd.enteredRegistrationData?.identificationNumber,
          familyName: vsd.enteredRegistrationData.familyName
        },
        patientData: data,
        institutionId: vsd.selectedInstitution.id,
        browserLanguage: getBrowserLanguage()
      };
      await getWebsocketInterface().send(socketData);
    }
  },
  rejoinGuestSession: async (): Promise<void> => {
    console.log('rejoin session');
    const vsd = ViewStateStoreApi.getState().viewStateData;

    if (vsd && vsd.state !== 'FINAL_SCREEN') {
      const socketData = {
        operation: 'REJOIN_GUEST_SESSION' as WebSocketOperation,
        visitId: vsd.visitId,
        guestData: vsd.guestData,
        institutionId: vsd.selectedInstitution.id,
        browserLanguage: getBrowserLanguage()
      };
      await getWebsocketInterface().send(socketData);
    }
  },
  initSession: async (
    mandatoryFieldForRegistration: RegistrationFormFields
  ): Promise<void> => {
    console.log('initSession');
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const identifier =
      mandatoryFieldForRegistration === 'IDENTIFICATION'
        ? vsd?.enteredRegistrationData?.identificationNumber
        : vsd?.enteredRegistrationData?.familyName;
    if (vsd) {
      set(state => (state.loading = true));
      await get().initWebsocket();
      if (ViewStateStoreApi.getState().viewStateData?.guestData?.urlToken) {
        if (
          vsd.visitId &&
          vsd.selectedInstitution &&
          vsd.guestData.urlToken &&
          vsd.guestData.rejoinCode
        ) {
          get().rejoinGuestSession();
        } else {
          get().registerGuest();
        }
      } else {
        if (vsd.queueId && identifier && vsd.selectedInstitution) {
          get().rejoinSession();
        } else {
          get().registerNewVisit();
        }
      }
    }
  },
  inviteGuests: async (): Promise<void> => {
    console.log('invite guests');
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const vInfo = get().visitInfo;
    if (vsd && vInfo) {
      try {
        const resp = await inviteGuestToCall({
          url: PortletStoreApi.getState().url,
          visitId: vInfo.visitId
        });
        set(state => (state.guestBeans = [resp.data]));
        set(state => (state.inviteLinkDrawerOpen = true));
        if ([resp.data].length === 0) {
          notify({
            message: intl(
              'virtualvisitpatientportlet.waitingroom.invite.modal.error'
            ),
            title: intl('virtualvisitpatientportlet.waitingroom.invite.error'),
            type: NotificationType.ERROR
          });
        }
      } catch (e) {
        console.log('Error inviting guest');
      }
    }
  },
  leaveWaitingList: async (): Promise<void> => {
    console.log('leave waiting list');
    try {
      set(state => (state.loading = false));
      const vsd = ViewStateStoreApi.getState().viewStateData;
      const vInfo = get().visitInfo;
      if (vsd && vInfo) {
        const socketData = {
          operation: 'END_WAITING' as WebSocketOperation,
          visitId: vInfo.visitId,
          institutionId: vsd.selectedInstitution.id
        };
        await getWebsocketInterface().send(socketData);
      }
    } finally {
      set(
        produce(state => {
          state.loading = false;
        })
      );
    }
  },
  sendVisitStatus: async (): Promise<void> => {
    try {
      set(state => (state.loading = false));
      const vsd = ViewStateStoreApi.getState().viewStateData;
      const vInfo = get().visitInfo;
      if (vsd && vInfo) {
        const socketData = {
          operation: 'SEND_STATUS' as WebSocketOperation,
          visitId: vInfo.visitId
        };
        await getWebsocketInterface().send(socketData);
      }
    } finally {
      set(state => (state.loading = false));
    }
  },
  recreateGuestLink: async (
    visitId: string,
    guestToken: string
  ): Promise<void> => {
    console.log('invite guest link');
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const vInfo = get().visitInfo;
    if (vsd && vInfo) {
      try {
        const resp = await recreateGuestLink({
          url: PortletStoreApi.getState().url,
          visitId: visitId,
          guestToken: guestToken
        });
        set(state => (state.guestBeans = [resp.data]));
        set(state => (state.inviteLinkDrawerOpen = true));
        if ([resp.data].length === 0) {
          notify({
            message: intl(
              'virtualvisitpatientportlet.waitingroom.invite.modal.error'
            ),
            title: intl('virtualvisitpatientportlet.waitingroom.invite.error'),
            type: NotificationType.ERROR
          });
        }
      } catch (e) {
        console.log('error recreating guest link');
      }
    }
  },
  grantBioAnalysisPermission: async () => {
    console.log('grantBioAnalysisPermission');
    const visitId = get().visitInfo?.visitId;
    if (!visitId) {
      return;
    }
    await grantBioAnalysisPermission({
      visitId: visitId,
      url: PortletStoreApi.getState().url
    });
  }
}));
